/** Hooks for a user's personal item inventory (not scoped to a single group). */
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';

const ITEMS_PAGE_SIZE = 20;
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CirculationMode, Item, PowerSource } from './useItems';
import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';
import { getPhotoSource } from '@/lib/photoLibraryTypes';
import {
  fetchLoansWithFallback,
  buildLoansByItemMap,
  synthesiseLoanStub,
} from '@/hooks/useLoanQueryUtils';

export interface ItemGroup {
  id: string;
  name: string;
}

export interface MyItem extends Item {
  groups: ItemGroup[];
  active_loan?: {
    id: string;
    borrower_id: string;
    borrowed_at: string;
    borrower_name?: string | null;
    due_at?: string | null;
    status?: string | null;
    return_initiated_by?: string | null;
    group_id?: string | null;
  } | null;
}

/**
 * Returns the current user's items with cursor-based pagination.
 *
 * Each item is enriched with the list of groups it has been shared to and any
 * active loan. Group names and active loans are resolved in parallel after
 * fetching the page of items to keep total query time low. If the loan query
 * fails due to a schema mismatch, a synthetic loan stub is used as fallback.
 *
 * @returns TanStack infinite query result. Each page contains `{ items: MyItem[], nextCursor }`.
 */
export function useMyItems() {
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: ['my-items', user?.id],
    queryFn: async ({
      pageParam = null,
    }: {
      pageParam: string | null;
    }): Promise<{ items: MyItem[]; nextCursor: string | null }> => {
      // Cursor-based pagination: `lt('created_at', pageParam)` skips items
      // already seen. Falls back to no filter on the first page.
      let query = supabase
        .from('items')
        .select('*')
        .eq('owner_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(ITEMS_PAGE_SIZE);

      if (pageParam) {
        query = query.lt('created_at', pageParam);
      }

      const { data: items, error } = await query;

      if (error) throw error;
      if (!items || items.length === 0) return { items: [], nextCursor: null };

      // Fetch item_groups and active loans in parallel
      const itemIds = items.map((i) => i.id);
      const [{ data: itemGroupLinks, error: itemGroupLinksError }, safeLoans] =
        await Promise.all([
          supabase.from('item_groups').select('item_id, group_id').in('item_id', itemIds),
          fetchLoansWithFallback(itemIds),
        ]);

      if (itemGroupLinksError) throw itemGroupLinksError;

      // Resolve group names from IDs — a second round-trip but isolated to
      // the unique set of group IDs across this page of items.
      const groupIds = [...new Set(itemGroupLinks?.map((ig) => ig.group_id) || [])];
      let groupMap = new Map<string, string>();

      if (groupIds.length > 0) {
        const { data: groups, error: groupsError } = await supabase
          .from('groups')
          .select('id, name')
          .in('id', groupIds);

        if (groupsError) throw groupsError;
        groupMap = new Map(groups?.map((g) => [g.id, g.name]) || []);
      }

      // Build item -> groups mapping (one item may belong to several groups)
      const itemGroupsMap = new Map<string, ItemGroup[]>();
      for (const link of itemGroupLinks || []) {
        const groupName = groupMap.get(link.group_id);
        if (groupName) {
          const existing = itemGroupsMap.get(link.item_id) || [];
          existing.push({ id: link.group_id, name: groupName });
          itemGroupsMap.set(link.item_id, existing);
        }
      }

      // Get borrower profiles
      const borrowerIds = [...new Set(safeLoans.map((l) => l.borrower_id))];
      let borrowerMap = new Map<string, string | null>();

      if (borrowerIds.length > 0) {
        const { data: borrowerProfiles, error: borrowerProfilesError } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', borrowerIds);

        if (borrowerProfilesError) throw borrowerProfilesError;
        borrowerMap = new Map(borrowerProfiles?.map((p) => [p.user_id, p.display_name]) || []);
      }

      // Map loans to items — sort so ACTIVE takes priority over other statuses
      // in case of duplicates (e.g. race between proposal and approval).
      const loansByItem = buildLoansByItemMap(safeLoans);

      const result = items.map((item) => {
        const loan = loansByItem.get(item.id);
        // Primary: use joined loan data. Fallback: if no loan found but the
        // item's own status says ON_LOAN, synthesise a minimal active_loan
        // stub so the UI still shows the correct state.
        const activeLoan = loan
          ? {
              id: loan.id,
              borrower_id: loan.borrower_id,
              borrowed_at: loan.borrowed_at,
              borrower_name: borrowerMap.get(loan.borrower_id) || null,
              due_at: loan.due_at || null,
              status: loan.status || null,
              return_initiated_by: loan.return_initiated_by ?? null,
              group_id: loan.group_id ?? null,
            }
          : synthesiseLoanStub(item);
        return {
          ...item,
          circulation_mode: item.circulation_mode as CirculationMode,
          power_source: item.power_source as PowerSource | null,
          groups: itemGroupsMap.get(item.id) || [],
          active_loan: activeLoan,
        };
      });

      const nextCursor =
        items.length === ITEMS_PAGE_SIZE ? items[items.length - 1].created_at : null;
      return { items: result, nextCursor };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null as string | null,
    enabled: !!user?.id,
  });
}

/**
 * Returns the current user's items that are not yet shared to a specific group.
 *
 * Used when the user wants to add an existing item to a group without creating a
 * duplicate. Filtering happens client-side after fetching the full personal
 * inventory and the group's existing item links.
 *
 * @param groupId - The group to exclude already-shared items from.
 * @returns TanStack query result containing an array of {@link MyItem} (with empty `groups`).
 */
export function useItemsNotInGroup(groupId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['items-not-in-group', groupId, user?.id],
    queryFn: async (): Promise<MyItem[]> => {
      // Get all user's non-frozen items (frozen items can't be shared to groups)
      const { data: items, error } = await supabase
        .from('items')
        .select('*')
        .eq('owner_id', user!.id)
        .eq('is_frozen', false)
        .order('name', { ascending: true });

      if (error) throw error;
      if (!items || items.length === 0) return [];

      // Get items already in this group
      const { data: existingLinks } = await supabase
        .from('item_groups')
        .select('item_id')
        .eq('group_id', groupId);

      const itemsInGroup = new Set(existingLinks?.map((l) => l.item_id) || []);

      // Filter out items already in this group
      const availableItems = items.filter((i) => !itemsInGroup.has(i.id));

      return availableItems.map((item) => ({
        ...item,
        circulation_mode: item.circulation_mode as CirculationMode,
        power_source: item.power_source as PowerSource | null,
        groups: [],
      }));
    },
    enabled: !!groupId && !!user?.id,
  });
}

/**
 * Creates a personal item without adding it to any group.
 *
 * The user can later share it to groups via {@link useAddItemToGroup}.
 *
 * @returns TanStack mutation accepting `{ name, description?, category?, brand?, circulationMode?, photoUrl?, powerSource? }`.
 * @throws On network offline, rate-limit exceeded, or tier item-cap reached (surfaced via toast).
 */
export function useCreatePersonalItem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      name,
      description,
      category,
      brand,
      circulationMode,
      photoUrl,
      powerSource,
    }: {
      name: string;
      description?: string;
      category?: string;
      brand?: string;
      circulationMode?: CirculationMode;
      photoUrl?: string;
      powerSource?: PowerSource;
    }) => {
      await assertOnline();
      const { data, error } = await supabase
        .from('items')
        .insert({
          owner_id: user!.id,
          name,
          description,
          category,
          brand,
          circulation_mode: circulationMode || 'RETURN_TO_OWNER_REQUIRED',
          photo_url: photoUrl,
          power_source: powerSource,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      posthog.capture('item_created', {
        category: variables.category,
        circulation_mode: variables.circulationMode,
        photo_source: getPhotoSource(variables.photoUrl),
      });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Shares an existing item to a group by inserting a row in `item_groups`.
 *
 * @returns TanStack mutation accepting `{ itemId, groupId }`.
 * @throws On network offline or Supabase insert error (surfaced via toast).
 */
export function useAddItemToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, groupId }: { itemId: string; groupId: string }) => {
      await assertOnline();
      const { error } = await supabase
        .from('item_groups')
        .insert({ item_id: itemId, group_id: groupId });

      if (error) throw error;
      return { itemId, groupId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['items', result.groupId] });
      queryClient.invalidateQueries({ queryKey: ['items-not-in-group'] });
      posthog.capture('item_added_to_group', { item_id: result.itemId, group_id: result.groupId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Removes an item from a group by deleting the matching `item_groups` row.
 *
 * The item itself is not deleted — only its association with the group.
 *
 * @returns TanStack mutation accepting `{ itemId, groupId }`.
 * @throws On network offline or Supabase delete error (surfaced via toast).
 */
export function useRemoveItemFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, groupId }: { itemId: string; groupId: string }) => {
      await assertOnline();
      const { error } = await supabase
        .from('item_groups')
        .delete()
        .eq('item_id', itemId)
        .eq('group_id', groupId);

      if (error) throw error;
      return { itemId, groupId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['items', result.groupId] });
      queryClient.invalidateQueries({ queryKey: ['items-not-in-group'] });
      posthog.capture('item_removed_from_group', { item_id: result.itemId, group_id: result.groupId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
